import type { DashboardWidgetType, WidgetConfig } from './dashboard-config.js';
import type { CanonicalWidgetData, ChartWidgetData } from './dashboard-data.js';

// Sample data and starting configs for every widget type, kept next to the schemas they must satisfy.
// The builder shows the samples in its Data tab and stores them as the `dataModel` of a remote source;
// the defaults are what a new widget starts as. Each table is a record over every type, so a new widget
// type or chart kind is a type error here until it has a sample and a default, and the conformance
// tests (backend-core `widget-conformance.test.ts`) check every entry against its schema.

export type ChartType = ChartWidgetData['type'];

/** Values the samples need from the UI layer. */
export interface ExampleOptions {
  /** Header colour for the data-table sample (the UI's first palette colour). */
  headerColor: string;
}

// ─── Chart samples ───────────────────────────────────────────────────────────

function simpleBarExample(type: 'bar' | 'stacked-bar'): ChartWidgetData {
  return {
    type,
    chart: {
      title: 'Example Chart',
      xAxisTitle: 'Category',
      yAxisTitle: 'Value',
      indexBy: 'x',
      data: [
        { x: 'Category A', y: 42 },
        { x: 'Category B', y: 67 },
        { x: 'Category C', y: 53 },
      ],
    },
  };
}

function multiBarExample(type: 'multiple-clustered-bar' | 'multiple-stacked-bar'): ChartWidgetData {
  return {
    type,
    chart: {
      title: 'Example Chart',
      xAxisTitle: 'Quarter',
      yAxisTitle: 'Value',
      indexBy: 'x',
      data: [
        {
          index: 'Series A',
          data: [
            { x: 'Q1', y: 42 },
            { x: 'Q2', y: 67 },
          ],
        },
        {
          index: 'Series B',
          data: [
            { x: 'Q1', y: 38 },
            { x: 'Q2', y: 55 },
          ],
        },
      ],
    },
  };
}

function kpiExample(type: 'kpi-widget' | 'progress-bar-widget' | 'semi-circle-progress-widget'): ChartWidgetData {
  return {
    type,
    title: 'KPI Overview',
    backgroundColor: '#ffffff',
    values: ['4,200', '87%', '$12.5K'],
    labels: ['Total Responses', 'Satisfaction Rate', 'Revenue'],
  };
}

/**
 * The sample the Data tab shows for each chart kind. A line chart's sample is a one-series
 * multiple-line chart, because that is the data the builder produces for a line chart.
 */
export const CHART_EXAMPLES: { [K in ChartType]: (options: ExampleOptions) => ChartWidgetData } = {
  bar: () => simpleBarExample('bar'),
  'stacked-bar': () => simpleBarExample('stacked-bar'),
  pie: () => ({
    type: 'pie',
    chart: {
      title: 'Example Chart',
      xAxisTitle: '',
      yAxisTitle: '',
      data: [
        { id: 'cat_a', label: 'Category A', value: 42 },
        { id: 'cat_b', label: 'Category B', value: 28 },
        { id: 'cat_c', label: 'Category C', value: 30 },
      ],
    },
  }),
  line: () => ({
    type: 'multiple-line',
    chart: {
      title: 'Example Chart',
      xAxisTitle: 'Category',
      yAxisTitle: 'Value',
      data: [
        {
          id: 'series',
          data: [
            { x: 'Category A', y: 42 },
            { x: 'Category B', y: 67 },
            { x: 'Category C', y: 53 },
          ],
        },
      ],
    },
  }),
  'multiple-line': () => ({
    type: 'multiple-line',
    chart: {
      title: 'Example Chart',
      xAxisTitle: 'Quarter',
      yAxisTitle: 'Value',
      data: [
        {
          id: 'Series A',
          data: [
            { x: 'Q1', y: 42 },
            { x: 'Q2', y: 67 },
          ],
        },
        {
          id: 'Series B',
          data: [
            { x: 'Q1', y: 38 },
            { x: 'Q2', y: 55 },
          ],
        },
      ],
    },
  }),
  'multiple-clustered-bar': () => multiBarExample('multiple-clustered-bar'),
  'multiple-stacked-bar': () => multiBarExample('multiple-stacked-bar'),
  'data-table': ({ headerColor }) => ({
    type: 'data-table',
    title: 'Example Table',
    headerColor,
    data: {
      columnNames: [['Category'], ['Value'], ['Change']],
      rowNames: [['1'], ['2'], ['3']],
      records: [
        ['Category A', '42', '+5%'],
        ['Category B', '67', '-2%'],
        ['Category C', '53', '+12%'],
      ],
    },
  }),
  'kpi-widget': () => kpiExample('kpi-widget'),
  'progress-bar-widget': () => kpiExample('progress-bar-widget'),
  'semi-circle-progress-widget': () => kpiExample('semi-circle-progress-widget'),
};

// ─── Widget data samples ─────────────────────────────────────────────────────

/** The sample data for each widget type that carries data. A chart's sample is the bar sample. */
export const WIDGET_DATA_EXAMPLES: {
  [T in CanonicalWidgetData['type']]: (options: ExampleOptions) => Extract<CanonicalWidgetData, { type: T }>;
} = {
  chart: (options) => ({ type: 'chart', data: CHART_EXAMPLES.bar(options) }),
  text: () => ({
    type: 'text',
    data: {
      content: '<p>Your HTML or plain text content here.</p>',
    },
  }),
  metric_card: () => ({
    type: 'metric_card',
    data: {
      values: ['4,200', '87%', '$12.5K'],
      labels: ['Total Responses', 'Satisfaction Rate', 'Revenue'],
    },
  }),
  code: () => ({
    type: 'code',
    data: { items: [], total: 0 },
  }),
  rep_dashboard: () => ({
    type: 'rep_dashboard',
    data: {
      summaryText: 'Here is a quick 2-minute summary of your performance metrics.',
      kpis: [
        {
          label: 'Calls per Day',
          value: '9.14',
          unit: 'HCPs',
          change: '+5.09%',
          changeLabel: 'prior QTD',
          changeType: 'positive',
        },
        {
          label: 'FEA',
          value: '68.31',
          unit: '%',
          change: '0%',
          changeLabel: 'prior QTD',
          changeType: 'neutral',
        },
        {
          label: 'Reach',
          value: '27.00',
          unit: '%',
          change: '0%',
          changeLabel: 'prior QTD',
          changeType: 'neutral',
        },
      ],
      opportunities: ['Example opportunity insight text.'],
      infoBoxText: 'Optional highlighted callout text.',
      tableRows: [
        {
          id: '12345678',
          name: 'Jane Doe',
          address: 'New York, NY',
          callGoal: 40,
          remainingCalls: 12,
          profileId: '12345678',
        },
      ],
    },
  }),
  quote: () => ({
    type: 'quote',
    data: {
      content: 'This is an example quote from a research participant.',
      speaker: 'Participant A',
    },
  }),
  segment_overview: () => ({
    type: 'segment_overview',
    data: {
      narrative: 'Example segment narrative.',
      commercialAction: 'Example commercial action.',
    },
  }),
};

// ─── Default configs ─────────────────────────────────────────────────────────

export const DEFAULT_CODE_WIDGET_JSX = `import { data } from './data';

export default function App() {
  return (
    <div className="p-4 flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-gray-800">My Widget</h2>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <pre className="text-xs text-gray-600 overflow-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    </div>
  );
}
`;

/** The config variant for a widget type (distributes over variants whose `type` is itself a union). */
type ConfigOfType<T extends DashboardWidgetType> = WidgetConfig extends infer W
  ? W extends { type: infer U; config: infer C }
    ? T extends U
      ? C
      : never
    : never
  : never;

/**
 * The config a new widget of each type starts with, or null for a type that can't be built from
 * defaults: saved-widget pointers need a library widget id, so they are only created by picking one.
 */
export const WIDGET_DEFAULT_CONFIGS: { [T in DashboardWidgetType]: ConfigOfType<T> | null } = {
  chart: { title: '', chartType: 'bar' },
  text: { title: '' },
  metric_card: { title: '' },
  code: { title: '', jsx: DEFAULT_CODE_WIDGET_JSX },
  rep_dashboard: { title: 'Rep Performance Dashboard' },
  quote: { title: '' },
  segment_overview: { title: 'Segment Overview' },
  library_widget: null,
  seg_widget: null,
  pj_widget: null,
  pj_media: null,
};
